/**
 * EventTabs — the ONE tab bar for event detail pages (Open Play + Tournaments).
 * Icon + label per tab, a one-line caption under the bar explaining what the
 * active tab does (so coordinators never have to ask), optional count badge.
 * One line that scrolls sideways on a phone (it used to wrap onto a second
 * row); the active tab is white with a red underline, the rest calm grey.
 *
 * tabs: [{ key, label, icon, caption?, badge?, hidden? }]
 * testPrefix keeps each page's existing data-testids stable
 * (open play: "op-tab", tournament: "tab").
 */
export default function EventTabs({ tabs, active, onChange, testPrefix = 'event-tab' }) {
  const visible = tabs.filter(t => !t.hidden);
  const current = tabs.find(t => t.key === active);
  return (
    <>
      <div role="tablist" className="flex gap-1 mb-2 overflow-x-auto"
        style={{ borderBottom: '1px solid #262626', scrollbarWidth: 'none' }}>
        {visible.map(({ key, label, icon: Icon, badge }) => {
          const on = active === key;
          return (
            <button key={key} role="tab" aria-selected={on} onClick={() => onChange(key)}
              data-testid={`${testPrefix}-${key}`}
              className="px-3.5 py-2.5 text-sm font-bold uppercase relative inline-flex items-center gap-1.5 whitespace-nowrap"
              style={{
                background: 'transparent',
                color: on ? '#fff' : '#8A8A8A',
                borderBottom: `2px solid ${on ? '#E63946' : 'transparent'}`,
                marginBottom: -1,
                fontFamily: 'Oswald, sans-serif',
                letterSpacing: 0.5,
                transition: 'color 160ms, border-color 160ms',
              }}>
              {Icon && <Icon className="h-3.5 w-3.5" />}
              {label}
              {badge > 0 && (
                <span className="ml-0.5 inline-flex items-center justify-center text-[10px] font-bold rounded-full px-1.5 min-w-[18px] h-[18px]"
                  style={{ background: '#262626', color: '#fff' }}>
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {current?.caption && (
        <p className="text-xs mb-4 px-1" style={{ color: '#6B6B6B' }} data-testid={`${testPrefix}-caption`}>
          {current.caption}
        </p>
      )}
    </>
  );
}
